import { APCGPaymentResponse, APCGRegistrationResponse, VerifyPaymentResponse, WorkshopCountResponse, APCGSupportTicketInsert, SupportTicketResponse, CategoryCountResponse, APCGZeroAmountRegistrationResponse, APCGEvent, APCGBankTransferResponse } from "../types/payments";
import apiClient from  "./apiClient";
import { saveApiErrorLog, saveRegistrationLog, extractErrorDetails } from "./loggingService";


/**
 * Initiates a payment and returns the PayFort redirection URL.
 * If PaymentMethod is "BANK TRANSFER", returns bank transfer details instead.
 */

export const initiatePayment = async (
  paymentData: FormData
): Promise<APCGPaymentResponse | APCGBankTransferResponse> => {
  try {
    const paymentMethod = paymentData.get("PaymentMethod")?.toString();
    
    // If bank transfer, expect bank transfer response
    if (paymentMethod === "BANK TRANSFER") {
      const response = await apiClient.post<APCGBankTransferResponse>(
        "/payment/APCGPayment",
        paymentData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    }
    
    // Otherwise, expect normal payment gateway response
    const response = await apiClient.post<APCGPaymentResponse>(
      "/payment/APCGPayment",
      paymentData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error("Payment initiation failed", error);
    
    // Extract detailed error information
    const { issue, reason } = extractErrorDetails(error);
    
    // Extract ALL registration data from FormData for comprehensive logging
    const extractFormData = (formData: FormData): string => {
      const data: Record<string, string> = {};
      
      // Extract all form fields (excluding files)
      for (const [key, value] of formData.entries()) {
        if (value instanceof File) {
          // For files, log file name and size instead of file content
          data[key] = `[FILE: ${value.name}, Size: ${value.size} bytes, Type: ${value.type || 'unknown'}]`;
        } else {
          // For regular form fields, log the value
          data[key] = value.toString();
        }
      }
      
      // Format as JSON string for readability
      return JSON.stringify(data, null, 2);
    };
    
    // Extract user information from FormData for logging
    const email = paymentData.get("emailAddress")?.toString() || "";
    const firstName = paymentData.get("firstName")?.toString() || "";
    const lastName = paymentData.get("lastName")?.toString() || "";
    const phoneNumber = paymentData.get("phoneNumber")?.toString() || "";
    const category = paymentData.get("category")?.toString() || "";
    const name = `${firstName} ${lastName}`.trim() || "Unknown";
    
    // Extract all registration data
    const allRegistrationData = extractFormData(paymentData);
    
    // Create professional issue message
    const issueMessage = `Payment Registration Failed - ${category || "Unknown Category"} | Error: ${issue}`;
    
    // Create detailed reason with ALL registration data
    const detailedReason = `${reason} | API: /payment/APCGPayment | Category: ${category || "N/A"} | User: ${email || "N/A"} | Full Registration Data: ${allRegistrationData}`;
    
    // Log error to registration log (Country will be auto-detected from IP in saveRegistrationLog)
    await saveRegistrationLog({
      Name: name,
      Email: email,
      MobileNo: phoneNumber,
      Issue: issueMessage,
      Reason: detailedReason,
      // Country will be auto-detected from actual IP geolocation, not from form field
    });
    
    throw error;
  }
};

/**
 * Verifies the payment response received from PayFort.
 */
export const verifyPayment2 = async (
  paymentParams: Record<string, string>
): Promise<VerifyPaymentResponse> => {
  const response = await apiClient.post<VerifyPaymentResponse>(
    "/ApcgPayment/APCGPaymentVerify",
    paymentParams
  );
  return response.data;
};

export const verifyPayment = async (
  paymentParams: Record<string, string>
): Promise<VerifyPaymentResponse> => {
  try {
    const response = await apiClient.post<VerifyPaymentResponse>(
      "/ApcgPayment/APCGPaymentVerify",
      paymentParams
    );
    return response.data;
  } catch (error) {
    console.error("🔴 Payment Verification Error:", error instanceof Error ? error.message : "Unknown error");
    throw error;
  }
};

export const getRegistrationDetails = async (
  apcgId: string
): Promise<APCGRegistrationResponse> => {
  const response = await apiClient.post<APCGRegistrationResponse>(
    `/payment/APCGRegistration?apcgId=${apcgId}`
  );
  return response.data;
};

/**
 * Gets the current workshop participant counts
 */
export const getWorkshopCounts = async (): Promise<WorkshopCountResponse> => {
  try {
    const response = await apiClient.get<WorkshopCountResponse>(
      "/Payment/GetWorkShopsCount"
    );
    
    // Return the new API structure
    return response.data;
  } catch (error: any) {
    console.error("Failed to fetch workshop counts:", error);
    
    // Extract detailed error information
    const { issue, reason } = extractErrorDetails(error);
    
    // Log error to API error log (dropdown error)
    await saveApiErrorLog({
      Issue: `Workshop Counts Dropdown Failed - ${issue}`,
      Reason: `${reason} | API Endpoint: /Payment/GetWorkShopsCount | Dropdown: Workshop Selection`,
      ApiEndpoint: "/Payment/GetWorkShopsCount",
      DropdownName: "Workshop Counts",
    });
    
    // Return default counts if API fails
    return {
      morningSessionCount: 0,
      afternoonSessionCount: 0,
      fulldaySessionCount: 0,
      morningWorkshops: {},
      afternoonWorkshops: {},
      fulldayWorkshops: {}
    };
  }
};

/**
 * Gets member details by APCG ID
 */
export const getMembersByAPCGId = async (apcgId: string): Promise<any[]> => {
  try {
    const response = await apiClient.get<any[]>(
      `/Payment/GetMembersByAPCGId/${apcgId}`
    );
    return response.data;
  } catch (error) {
    console.error("Failed to fetch member details:", error);
    throw error;
  }
};

/**
 * Creates a support ticket
 */
export const createSupportTicket = async (
  supportTicketData: APCGSupportTicketInsert
): Promise<SupportTicketResponse> => {
  try {
    const response = await apiClient.post<SupportTicketResponse>(
      "/payment/CreateSupportTicket",
      supportTicketData
    );
    return response.data;
  } catch (error: any) {
    console.error("Support ticket creation failed", error);
    
    // Extract detailed error information
    const { issue, reason } = extractErrorDetails(error);
    
    // Extract user information from support ticket data
    const email = supportTicketData.EMAIL || "";
    const name = supportTicketData.FULL_NAME || "Unknown";
    const phoneNumber = supportTicketData.MOBILE_NUMBER || "";
    const ticketIssue = supportTicketData.ISSUE || "";
    
    // Create professional issue message
    const issueMessage = `Support Ticket Creation Failed | Error: ${issue}`;
    
    // Create detailed reason with context
    const detailedReason = `${reason} | API: /payment/CreateSupportTicket | User Issue: ${ticketIssue || "N/A"} | User Email: ${email || "N/A"}`;
    
    // Log error to registration log (since it's a save operation)
    await saveRegistrationLog({
      Name: name,
      Email: email,
      MobileNo: phoneNumber,
      Issue: issueMessage,
      Reason: detailedReason,
    });
    
    throw error;
  }
};

/**
 * Gets the current count for all special categories
 * Returns counts for both "Ministry of Education and General Directorate of Jeddah" and "MAWHIBA"
 */
export const getCategoryCount = async (): Promise<CategoryCountResponse> => {
  try {
    const response = await apiClient.get<CategoryCountResponse>(
      `/Payment/GetCategoryCount`
    );
    return response.data;
  } catch (error: any) {
    console.error("Failed to fetch category counts:", error);
    
    // Extract detailed error information
    const { issue, reason } = extractErrorDetails(error);
    
    // Log error to API error log (dropdown error)
    await saveApiErrorLog({
      Issue: `Category Count Dropdown Failed - ${issue}`,
      Reason: `${reason} | API Endpoint: /Payment/GetCategoryCount | Dropdown: Category Selection`,
      ApiEndpoint: `/Payment/GetCategoryCount`,
      DropdownName: "Category Count",
    });
    
    throw error;
  }
};

/**
 * Registers for zero amount (free) category
 */
export const registerZeroAmount = async (
  registrationData: FormData
): Promise<APCGZeroAmountRegistrationResponse> => {
  try {
    const response = await apiClient.post<APCGZeroAmountRegistrationResponse>(
      "/Payment/APCGZeroAmountRegistration",
      registrationData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error("Zero amount registration failed", error);
    
    // Extract detailed error information
    const { issue, reason } = extractErrorDetails(error);
    
    // Extract ALL registration data from FormData for comprehensive logging
    const extractFormData = (formData: FormData): string => {
      const data: Record<string, string> = {};
      
      // Extract all form fields (excluding files)
      for (const [key, value] of formData.entries()) {
        if (value instanceof File) {
          // For files, log file name and size instead of file content
          data[key] = `[FILE: ${value.name}, Size: ${value.size} bytes, Type: ${value.type || 'unknown'}]`;
        } else {
          // For regular form fields, log the value
          data[key] = value.toString();
        }
      }
      
      // Format as JSON string for readability
      return JSON.stringify(data, null, 2);
    };
    
    // Extract user information from FormData for logging
    const email = registrationData.get("emailAddress")?.toString() || "";
    const firstName = registrationData.get("firstName")?.toString() || "";
    const lastName = registrationData.get("lastName")?.toString() || "";
    const phoneNumber = registrationData.get("phoneNumber")?.toString() || "";
    const category = registrationData.get("category")?.toString() || "";
    const name = `${firstName} ${lastName}`.trim() || "Unknown";
    
    // Extract all registration data
    const allRegistrationData = extractFormData(registrationData);
    
    // Create professional issue message
    const issueMessage = `Free Registration Failed - ${category || "Zero Amount Category"} | Error: ${issue}`;
    
    // Create detailed reason with ALL registration data
    const detailedReason = `${reason} | API: /Payment/APCGZeroAmountRegistration | Category: ${category || "N/A"} | Registration Type: Free/Zero Amount | User Email: ${email || "N/A"} | Full Registration Data: ${allRegistrationData}`;
    
    // Log error to registration log (Country will be auto-detected from IP in saveRegistrationLog)
    await saveRegistrationLog({
      Name: name,
      Email: email,
      MobileNo: phoneNumber,
      Issue: issueMessage,
      Reason: detailedReason,
      // Country will be auto-detected from actual IP geolocation, not from form field
    });
    
    throw error;
  }
};

/**
 * Registers for a field trip (AlUla) using APCGZeroAmountRegistration endpoint
 */
export const registerTrip = async (
  tripData: FormData
): Promise<APCGZeroAmountRegistrationResponse> => {
  try {
    const response = await apiClient.post<APCGZeroAmountRegistrationResponse>(
      "/Payment/APCGZeroAmountRegistration",
      tripData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error("Trip registration failed", error);
    
    // Extract detailed error information
    const { issue, reason } = extractErrorDetails(error);
    
    // Extract ALL registration data from FormData for comprehensive logging
    const extractFormData = (formData: FormData): string => {
      const data: Record<string, string> = {};
      
      // Extract all form fields (excluding files)
      for (const [key, value] of formData.entries()) {
        if (value instanceof File) {
          // For files, log file name and size instead of file content
          data[key] = `[FILE: ${value.name}, Size: ${value.size} bytes, Type: ${value.type || 'unknown'}]`;
        } else {
          // For regular form fields, log the value
          data[key] = value.toString();
        }
      }
      
      // Format as JSON string for readability
      return JSON.stringify(data, null, 2);
    };
    
    // Extract user information from FormData for logging
    const email = tripData.get("emailAddress")?.toString() || "";
    const firstName = tripData.get("firstName")?.toString() || "";
    const lastName = tripData.get("lastName")?.toString() || "";
    const phoneNumber = tripData.get("phoneNumber")?.toString() || "";
    
    // Extract trip/event information
    const eventIds: string[] = [];
    tripData.forEach((value, key) => {
      if (key.startsWith("EventId[") && value) {
        eventIds.push(value.toString());
      }
    });
    const tripInfo = eventIds.length > 0 ? `Trips: ${eventIds.join(", ")}` : "Field Trip Registration";
    
    const name = `${firstName} ${lastName}`.trim() || "Unknown";
    
    // Extract all registration data
    const allRegistrationData = extractFormData(tripData);
    
    // Create professional issue message
    const issueMessage = `Field Trip Registration Failed - ${tripInfo} | Error: ${issue}`;
    
    // Create detailed reason with ALL registration data
    const detailedReason = `${reason} | API: /Payment/APCGZeroAmountRegistration | Registration Type: Field Trip | Selected Trips: ${eventIds.length > 0 ? eventIds.join(", ") : "N/A"} | User Email: ${email || "N/A"} | Full Registration Data: ${allRegistrationData}`;
    
    // Log error to registration log (Country will be auto-detected from IP in saveRegistrationLog)
    await saveRegistrationLog({
      Name: name,
      Email: email,
      MobileNo: phoneNumber,
      Issue: issueMessage,
      Reason: detailedReason,
      // Country will be auto-detected from actual IP geolocation, not from form field
    });
    
    throw error;
  }
};

/**
 * Gets all APCG events (field trips, gala dinner, etc.)
 */
export const getAPCGEvents = async (): Promise<APCGEvent[]> => {
  try {
    const response = await apiClient.get<APCGEvent[]>(
      "/Payment/GetAPCGEvents"
    );
    return response.data;
  } catch (error: any) {
    console.error("Failed to fetch APCG events:", error);
    
    // Extract detailed error information
    const { issue, reason } = extractErrorDetails(error);
    
    // Log error to API error log (dropdown error)
    await saveApiErrorLog({
      Issue: `Events Dropdown Failed - ${issue}`,
      Reason: `${reason} | API Endpoint: /Payment/GetAPCGEvents | Dropdown: Field Trips/Events Selection`,
      ApiEndpoint: "/Payment/GetAPCGEvents",
      DropdownName: "APCG Events",
    });
    
    throw error;
  }
};

/**
 * Gets event details by APCG ID
 * Returns events with memberId: "-1" for group leader/main registrant
 * and actual memberId for group members
 */
export const getEventDetailsByAPCGId = async (apcgId: string): Promise<any[]> => {
  try {
    const response = await apiClient.get<any[]>(
      `/Payment/GetEventDetailsByAPCGId/${apcgId}`
    );
    return response.data;
  } catch (error) {
    console.error("Failed to fetch event details:", error);
    throw error;
  }
};

/**
 * Gets APCG members by email address
 * Returns empty array if user not found (404) instead of throwing error
 */
export const getAPCGMembersByEmail = async (email: string): Promise<any[]> => {
  try {
    const response = await apiClient.get<any[]>(
      `/Payment/GetAPCGMembersByEmail?email=${encodeURIComponent(email)}`
    );
    return response.data || [];
  } catch (error: any) {
    // 404 means user not found, which is valid - return empty array
    if (error.response && error.response.status === 404) {
      return [];
    }
    // For other errors, log but still return empty array to allow form submission
    console.error("Failed to fetch APCG members by email:", error);
    
    // Extract detailed error information
    const { issue, reason } = extractErrorDetails(error);
    
    // Log error to API error log (email fetch error)
    await saveApiErrorLog({
      Issue: `Email Lookup Failed - Get Members by Email | Error: ${issue}`,
      Reason: `${reason} | API Endpoint: /Payment/GetAPCGMembersByEmail | Email: ${email || "N/A"} | Purpose: Fetching APCG members`,
      ApiEndpoint: `/Payment/GetAPCGMembersByEmail?email=${encodeURIComponent(email)}`,
      DropdownName: "Email Lookup - Members",
    });
    
    return [];
  }
};

/**
 * Gets APCG registration by email address
 * Returns empty array if user not found (404) instead of throwing error
 */
export const getAPCGRegistrationByEmail = async (email: string): Promise<any[]> => {
  try {
    const response = await apiClient.get<any[]>(
      `/Payment/GetAPCGRegistrationByEmail?email=${encodeURIComponent(email)}`
    );
    return response.data || [];
  } catch (error: any) {
    // 404 means user not found, which is valid - return empty array
    if (error.response && error.response.status === 404) {
      return [];
    }
    // For other errors, log but still return empty array to allow form submission
    console.error("Failed to fetch APCG registration by email:", error);
    
    // Extract detailed error information
    const { issue, reason } = extractErrorDetails(error);
    
    // Log error to API error log (email fetch error)
    await saveApiErrorLog({
      Issue: `Email Lookup Failed - Get Registration by Email | Error: ${issue}`,
      Reason: `${reason} | API Endpoint: /Payment/GetAPCGRegistrationByEmail | Email: ${email || "N/A"} | Purpose: Checking existing registration`,
      ApiEndpoint: `/Payment/GetAPCGRegistrationByEmail?email=${encodeURIComponent(email)}`,
      DropdownName: "Email Lookup - Registration",
    });
    
    return [];
  }
};

/**
 * Gets paid event details (field trips) by email address
 * Returns empty array if user not found (404) instead of throwing error
 * Response format: [{ registrationId, eventId, eventName, email, isPaid }]
 */
export const getPaidEventDetailsByEmail = async (email: string): Promise<any[]> => {
  try {
    const response = await apiClient.get<any[]>(
      `/Payment/GetPaidEventDetailsByEmail?email=${encodeURIComponent(email)}`
    );
    return response.data || [];
  } catch (error: any) {
    // 404 means user not found, which is valid - return empty array
    if (error.response && error.response.status === 404) {
      return [];
    }
    // For other errors, log but still return empty array to allow form submission
    console.error("Failed to fetch paid event details by email:", error);
    
    // Extract detailed error information
    const { issue, reason } = extractErrorDetails(error);
    
    // Log error to API error log (email fetch error)
    await saveApiErrorLog({
      Issue: `Email Lookup Failed - Get Paid Event Details by Email | Error: ${issue}`,
      Reason: `${reason} | API Endpoint: /Payment/GetPaidEventDetailsByEmail | Email: ${email || "N/A"} | Purpose: Checking existing field trip registrations`,
      ApiEndpoint: `/Payment/GetPaidEventDetailsByEmail?email=${encodeURIComponent(email)}`,
      DropdownName: "Email Lookup - Paid Event Details",
    });
    
    return [];
  }
};